import { Head, router, usePage } from "@inertiajs/react";
import { type ReactNode, useEffect } from "react";
import { Toaster, toast } from "sonner";

import type { SharedProps } from "@/types";

import Footer from "./Footer";
import Header from "./Header";

function showFlash(f: SharedProps["flash"]) {
  if (f?.notice) {
    toast.success(f.notice, {
      style: {
        background: "#F0FDF4",
        borderColor: "#16A34A",
        color: "#166534",
      },
      duration: 4000,
    });
  }
  if (f?.alert) {
    toast.error(f.alert, {
      style: {
        background: "#FEF2F2",
        borderColor: "#961A1A",
        color: "#961A1A",
      },
      duration: 5000,
    });
  }
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const { flash } = usePage<SharedProps>().props;

  // Show flash already present on the initial page load.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once at mount
  useEffect(() => {
    showFlash(flash);
  }, []);

  // Show flash on every subsequent Inertia visit. 'success' fires after every
  // successful page response (including same-component redirects), and the
  // flash is read directly from the event so it's always fresh.
  useEffect(() => {
    return router.on("success", (event) => {
      showFlash((event.detail.page.props as SharedProps).flash);
    });
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Head title="Fundación Los Tilos" />
      <Toaster
        position="top-right"
        closeButton
        toastOptions={{
          classNames: {
            toast: "font-sans rounded-xl p-5 gap-3 min-w-[320px] sm:min-w-[420px]",
            title: "text-base sm:text-lg font-medium",
            description: "text-sm sm:text-base",
            icon: "h-5 w-5",
          },
        }}
      />
      <Header />

      <main className="flex flex-1 flex-col">{children}</main>

      <Footer />
    </div>
  );
}
